/**
 * Générateur — Il résout des problèmes en utilisant la proportionnalité dans
 * le cadre de la géométrie.
 * Attendu ID : 220 | Niveau : 3ème | Domaine : Proportionnalité (d16, O8)
 *
 * Source doc prof (OGD FONCTIONS 3e séance B1S2 + ESPACE/GEOMETRIE D4S1-D4S6).
 * ANTI-DOUBLON (décision de plan) : ce gen porte Thalès CALCULATOIRE,
 * triangles semblables et homothétie-longueurs ; le thème #11 (gen_240-243)
 * portera Thalès DÉMONSTRATION et les raisonnements.
 *
 * Plan de cartes auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *   « thales »     — triangles emboîtés (svgThales 4e RÉUTILISÉ), 2 longueurs
 *   « papillon »   — configuration papillon (texte, données complètes)
 *   « telepherique » — Thalès « réel » : l'altitude de l'arrêt (brevet)
 *   « rapports »   — réciproque : calculer les deux quotients (décimaux exacts)
 *   « conclusion » — que peut-on conclure ? (QCM, égalité qui BASCULE)
 *   « semblables » — côtés proportionnels (rapport) / deux angles égaux
 *   « homothetie » — l'homothétie multiplie les longueurs, conserve les angles
 */

import {
    randomChoice, randInt, shuffle, tex,
    makeQcm, makeTexteTrou,
} from '../../../utils';
import { svgThales } from '../../4eme/geometrie-plane/geometrie';
import { imgElement } from '../../5eme/statistiques/svgStats';

const ATTENDU_ID = 220;

const L_THALES = 'Calculer des longueurs avec Thalès (triangles emboîtés)';
const L_PAPILLON = 'Calculer une longueur (configuration papillon)';
const L_TELEPHERIQUE = 'Thalès en situation : l\'altitude de l\'arrêt';
const L_RAPPORTS = 'Réciproque de Thalès : calculer les quotients';
const L_CONCLUSION = 'Réciproque de Thalès : conclure';
const L_SEMBLABLES = 'Reconnaître des triangles semblables';
const L_HOMOTHETIE = 'Homothétie : longueurs multipliées, angles conservés';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const frNb = (x) => String(Math.round(x * 100) / 100).replace('.', ',');

// ═════════ « thales » — triangles emboîtés (figure svgThales) ═════════

const RAPPORTS_T = [[1, 2], [2, 5], [3, 5], [3, 4], [1, 4], [2, 3]];

const genThalesTrous = () => {
    // t = n/d ; toutes les longueurs à UNE décimale exacte par construction.
    let n; let d; let SB; let HB; let SM; let SI; let MI; let SH;
    for (let essai = 0; essai < 80; essai++) {
        [n, d] = randomChoice(RAPPORTS_T);
        SB = d * randomChoice([1, 1.5, 2, 2.5]);           // grand côté droit
        HB = d * randomChoice([1, 1.5, 2]);                // grande base
        SH = d * randomChoice([1, 1.5, 2, 2.5]);           // grand côté gauche
        SI = (SB * n) / d;                                  // petit côté droit (donné)
        MI = (HB * n) / d;                                  // petite base (CHERCHÉE)
        SM = (SH * n) / d;                                  // petit côté gauche (donné)
        const ok = [SB, HB, SH, SI, MI, SM].every(x => Math.round(x * 10) === x * 10 && x >= 1);
        if (ok && SB !== SH && MI !== SM && MI !== SH) break;
        MI = 0;
    }
    if (!MI) { n = 3; d = 5; SB = 10; HB = 7.5; SH = 7.5; SI = 6; MI = 4.5; SM = 4.5; }
    // Lettres : S sommet ; grande base HB ; M ∈ [SH], I ∈ [SB].
    const svg = svgThales({ noms: { E: 'S', F: 'H', D: 'B', G: 'M', I: 'I' }, t: n / d });
    const phrase = `MI = {{${frNb(MI)}}} cm ; SH = {{${frNb(SH)}}} cm.`;
    const payload = makeTexteTrou(phrase,
        `<p>S est le sommet ; M est sur [SH], I est sur [SB] et (MI) est parallèle à (HB).</p>`
        + `<p>On donne : SI = ${frNb(SI)} cm ; SB = ${frNb(SB)} cm ; HB = ${frNb(HB)} cm ; SM = ${frNb(SM)} cm.</p>`
        + '<p><b>Calcule MI puis SH avec le théorème de Thalès.</b> Utilise une virgule si le '
        + 'résultat n\'est pas entier.</p>',
        { ...opts(L_THALES), mode: 'saisie' });
    payload.elements_additionnels = imgElement(svg);
    payload._meta.params = { n, d, SB, HB, SH, SI, MI, SM };
    return payload;
};

// ═════════ « papillon » — texte, données complètes ═════════

const genPapillonTrous = () => {
    let MA; let AC; let BC; let MN;
    for (let essai = 0; essai < 80; essai++) {
        const [n, d] = randomChoice(RAPPORTS_T);
        AC = d * randomChoice([1, 1.5, 2, 3]);
        MA = (AC * n) / d;
        BC = randomChoice([2, 2.4, 2.8, 3, 3.6, 4, 4.4]);
        MN = (BC * n) / d;
        const ok = [MA, AC, BC, MN].every(x => Math.round(x * 100) === x * 100 && x >= 0.5);
        if (ok && Math.round(MN * 10) === MN * 10) break;
        MN = 0;
    }
    if (!MN) { MA = 0.6; AC = 1.8; BC = 2.1; MN = 0.7; }   // repli (doc Ex.7)
    const phrase = `MN = {{${frNb(MN)}}} cm.`;
    const payload = makeTexteTrou(phrase,
        '<p>Les points M, A, C sont alignés ; les points N, A, B sont alignés ; les droites (MN) et (BC) '
        + 'sont parallèles : les deux triangles sont de part et d\'autre de A.</p>'
        + `<p>On donne : MA = ${frNb(MA)} cm ; AC = ${frNb(AC)} cm ; BC = ${frNb(BC)} cm.</p>`
        + '<p><b>Calcule MN.</b> Utilise une virgule si le résultat n\'est pas entier.</p>',
        { ...opts(L_PAPILLON), mode: 'saisie' });
    payload._meta.params = { MA, AC, BC, MN };
    return payload;
};

// ═════════ « telepherique » — l'altitude de l'arrêt ═════════

const genTelepheriqueTrous = () => {
    // alt = H × d / L, ENTIER par construction : L = m·k, d = n·k, H multiple de m.
    let L; let d; let H; let alt;
    for (let essai = 0; essai < 60; essai++) {
        const m = randomChoice([3, 4, 5, 6]);
        const n = randInt(1, m - 1);
        const k = randomChoice([80, 100, 120, 150]);
        L = m * k; d = n * k;
        H = m * randomChoice([50, 60, 80, 100, 120, 146]);
        alt = (H * d) / L;
        if (Number.isInteger(alt) && H < L && alt >= 50) break;
        alt = 0;
    }
    if (!alt) { L = 1200; d = 400; H = 600; alt = 200; }
    const engin = randomChoice(['téléphérique', 'télésiège', 'funiculaire']);
    const phrase = `Altitude de l'arrêt : {{${alt}}} m.`;
    const payload = makeTexteTrou(phrase,
        `<p>La ligne d'un ${engin} mesure ${L} m, du bas B (altitude 0 m) à la gare G (altitude ${H} m), `
        + `en ligne droite. Après avoir parcouru ${d} m depuis B, la cabine marque un arrêt en D.</p>`
        // « c'est Thalès dans le triangle de la pente » livrait la modélisation,
        // qui est précisément ce que cette carte de brevet fait chercher.
        + '<p><b>À quelle altitude se trouve l\'arrêt ?</b></p>',
        { ...opts(L_TELEPHERIQUE), mode: 'saisie' });
    payload._meta.params = { L, d, H, alt, engin };
    return payload;
};

// ═════════ « rapports » + « conclusion » — réciproque de Thalès ═════════

// Quadruplet (RT, RP, RM, RS) : quotients décimaux exacts, égaux ou non.
const tirerReciproque = () => {
    const q1 = randomChoice([0.4, 0.5, 0.6, 0.75, 0.8]);   // RT/RP
    const egaux = randInt(0, 1) === 1;
    const q2 = egaux ? q1 : randomChoice([0.4, 0.5, 0.6, 0.75, 0.8].filter(x => x !== q1));
    const RP = randomChoice([4, 6, 8, 10, 12]);
    const RS = randomChoice([4, 6, 8, 10]);
    const RT = q1 * RP; const RM = q2 * RS;
    const ok = [RT, RM].every(x => Math.round(x * 10) === x * 10);
    return ok ? { q1, q2, egaux, RP, RS, RT, RM } : { q1: 0.75, q2: 0.75, egaux: true, RP: 8, RS: 6, RT: 6, RM: 4.5 };
};

const introReciproque = (v) => '<p>R, T, P sont alignés dans cet ordre, ainsi que R, M, S. '
    + `On donne : RT = ${frNb(v.RT)} ; RP = ${frNb(v.RP)} ; RM = ${frNb(v.RM)} ; RS = ${frNb(v.RS)}.</p>`;

const genRapportsTrous = () => {
    const v = tirerReciproque();
    const phrase = `$\\dfrac{RT}{RP} = {{${frNb(v.q1)}}} \\qquad \\dfrac{RM}{RS} = {{${frNb(v.q2)}}}$`;
    const payload = makeTexteTrou(phrase,
        introReciproque(v)
        + '<p><b>Calcule les deux quotients.</b> Écris-les en écriture décimale.</p>',
        { ...opts(L_RAPPORTS), mode: 'saisie' });
    payload._meta.params = v;
    return payload;
};

const genConclusionQcm = () => {
    const v = tirerReciproque();
    const chOui = 'Les droites (TM) et (PS) sont parallèles, d\'après la réciproque du théorème de Thalès.';
    const chNon = 'Les droites (TM) et (PS) ne sont pas parallèles : les quotients sont différents.';
    const chRien = 'On ne peut rien conclure sans mesurer les angles.';
    const correct = v.egaux ? chOui : chNon;
    const payload = makeQcm([chOui, chNon, chRien], correct,
        introReciproque(v)
        + `<p>On calcule : ${tex(`\\dfrac{RT}{RP} = ${frNb(v.q1).replace(',', '{,}')}`)} et `
        + `${tex(`\\dfrac{RM}{RS} = ${frNb(v.q2).replace(',', '{,}')}`)}.</p>`
        + '<p><b>Que peut-on conclure ?</b></p>',
        opts(L_CONCLUSION));
    payload._meta.params = v;
    return payload;
};

// ═════════ « semblables » — deux critères ═════════

const genSemblablesTrous = () => {
    if (randInt(0, 1)) {
        // Côtés proportionnels : rapport k décimal exact.
        let k; let sides; let sides2;
        for (let essai = 0; essai < 60; essai++) {
            k = randomChoice([0.4, 0.5, 0.75, 1.5, 2, 2.5]);
            sides = shuffle([[10, 7.5, 5], [14, 25, 28], [8, 12, 16], [6, 9, 12], [10, 16, 22]])[0];
            sides2 = sides.map(x => x * k);
            if (sides2.every(x => Math.round(x * 10) === x * 10)) break;
            sides2 = null;
        }
        if (!sides2) { k = 0.5; sides = [10, 7.5, 5]; sides2 = [5, 3.75, 2.5]; }
        const phrase = `Chaque quotient vaut {{${frNb(k)}}} : les triangles sont semblables, de rapport {{${frNb(k)}}}.`;
        const payload = makeTexteTrou(phrase,
            `<p>Le triangle ABC a pour côtés ${sides.map(frNb).join(' cm, ')} cm ; le triangle DEF a pour côtés `
            + `${sides2.map(frNb).join(' cm, ')} cm, donnés dans le même ordre.</p>`
            + '<p><b>Calcule les deux quotients demandés, puis conclus par OUI ou NON.</b> '
            + 'Écris les quotients en écriture décimale.</p>',
            { ...opts(L_SEMBLABLES), mode: 'saisie', strict: false });
        // (même résidu de variante inachevée que la branche angles : un
        // `semblable` jamais défini crashait 1 tirage sur 2 — S-CRASH)
        payload._meta.params = { critere: 'cotes', k, sides };
        return payload;
    }
    // Deux angles égaux : 3e angle calculé.
    let a1; let a2;
    do { a1 = randInt(20, 70); a2 = randInt(35, 95); } while (a1 + a2 >= 150 || a1 === a2);
    const a3 = 180 - a1 - a2;
    const phrase = `Troisième angle du premier triangle : {{${a3}}}° — les deux triangles ont deux paires d'angles égaux : ils sont semblables.`;
    const payload = makeTexteTrou(phrase,
        // ${a3} (le 3e angle du premier triangle) : les deux triangles ont
        // ainsi deux paires d'angles égaux — cohérent avec la conclusion
        // « ils sont semblables » codée dans la phrase. (Un `${b2}` jamais
        // défini — amorce de variante OUI/NON inachevée — crashait la carte
        // à 100 % des tirages : S-CRASH « b2 is not defined ».)
        `<p>Un premier triangle a des angles de ${a1}° et ${a2}°. Un second triangle a des angles de ${a1}° et ${a3}°.</p>`
        + '<p><b>Calcule le troisième angle du premier triangle, puis conclus.</b></p>',
        { ...opts(L_SEMBLABLES), mode: 'saisie', strict: false });
    payload._meta.params = { critere: 'angles', a1, a2, a3 };
    return payload;
};

// ═════════ « homothetie » — longueurs × k, angles conservés ═════════

const genHomothetieTrous = () => {
    let k; let l1; let l2;
    for (let essai = 0; essai < 60; essai++) {
        k = randomChoice([2, 3, 1.5, 2.5, 0.5, 1.25]);
        l1 = randomChoice([2, 2.4, 3, 3.6, 4, 4.8, 6]);
        l2 = randomChoice([1.3, 2.3, 2.7, 3.1, 4.2, 5]);
        const ok = [l1 * k, l2 * k].every(x => Math.round(x * 100) === x * 100);
        if (ok && l1 !== l2) break;
        k = 0;
    }
    if (!k) { k = 3; l1 = 2.3; l2 = 2.7; }
    const angle = randomChoice([35, 48, 60, 72, 84, 105]);
    const kAff = frNb(k);
    const phrase = `A'B' = {{${frNb(l1 * k)}}} cm ; B'C' = {{${frNb(l2 * k)}}} cm ; l'angle A'B'C' mesure {{${angle}}}°.`;
    const payload = makeTexteTrou(phrase,
        `<p>A'B'C' est l'image du triangle ABC par une homothétie de rapport ${kAff}. `
        + `On donne AB = ${frNb(l1)} cm, BC = ${frNb(l2)} cm et l'angle ABC mesure ${angle}°.</p>`
        // Le « Rappel » énonçait les deux propriétés de l'homothétie qui sont
        // exactement les trois réponses attendues.
        + '<p><b>Détermine A\'B\', B\'C\' et l\'angle A\'B\'C\'.</b></p>',
        { ...opts(L_HOMOTHETIE), mode: 'saisie' });
    payload._meta.params = { k, l1, l2, angle };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    thales_texte_trou_saisie: genThalesTrous,
    papillon_texte_trou_saisie: genPapillonTrous,
    telepherique_texte_trou_saisie: genTelepheriqueTrous,
    rapports_texte_trou_saisie: genRapportsTrous,
    conclusion_qcm_unique: genConclusionQcm,
    semblables_texte_trou_saisie: genSemblablesTrous,
    homothetie_texte_trou_saisie: genHomothetieTrous,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'thales', label: L_THALES, formatsList: buildFormats('thales') },
    { id: 'papillon', label: L_PAPILLON, formatsList: buildFormats('papillon') },
    { id: 'telepherique', label: L_TELEPHERIQUE, formatsList: buildFormats('telepherique') },
    { id: 'rapports', label: L_RAPPORTS, formatsList: buildFormats('rapports') },
    { id: 'conclusion', label: L_CONCLUSION, formatsList: buildFormats('conclusion') },
    { id: 'semblables', label: L_SEMBLABLES, formatsList: buildFormats('semblables') },
    { id: 'homothetie', label: L_HOMOTHETIE, formatsList: buildFormats('homothetie') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
